import React, { useState, useEffect, useRef } from 'react';
import { TextField, Grid, FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormContainer from '../../components/FormContainer';
import { addVillage, updateVillage, fetchDistricts, fetchTalukasByDistrict } from '../../utils/api';

const VillageForm = ({ editVillage = null, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(true);
  const [isLoadingTalukas, setIsLoadingTalukas] = useState(false);
  const isEditMode = !!editVillage;
  const onSuccessTimerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (onSuccessTimerRef.current != null) {
        clearTimeout(onSuccessTimerRef.current);
        onSuccessTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadInitialData = async () => {
      try {
        const districtsResponse = await fetchDistricts();
        if (cancelled) return;
        setDistricts(districtsResponse.data);
        setIsLoadingDistricts(false);

        if (isEditMode && editVillage.taluka?.district?.districtId) {
          setIsLoadingTalukas(true);
          const talukasResponse = await fetchTalukasByDistrict(editVillage.taluka.district.districtId);
          if (cancelled) return;
          setTalukas(talukasResponse.data);
          setIsLoadingTalukas(false);
        }
      } catch (error) {
        console.error('Error loading form data:', error);
        if (!cancelled) {
          setErrorMessage('Failed to load form data. Please refresh the page.');
          setIsLoadingDistricts(false);
          setIsLoadingTalukas(false);
        }
      }
    };

    loadInitialData();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, editVillage]);

  const validationSchema = Yup.object({
    villageName: Yup.string().required('Village name is required').max(100, 'Village name must be at most 100 characters'),
    districtId: Yup.number().required('District is required'),
    talukaId: Yup.number().required('Taluka is required'),
  });

  const formik = useFormik({
    initialValues: {
      villageName: editVillage ? editVillage.villageName : '',
      districtId: editVillage?.taluka?.district ? editVillage.taluka.district.districtId : '',
      talukaId: editVillage?.taluka ? editVillage.taluka.talukaId : '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      setSuccessMessage(null);
      setErrorMessage(null);

      try {
        const villageData = {
          villageName: values.villageName,
          taluka: {
            talukaId: values.talukaId
          }
        };

        if (isEditMode) {
          await updateVillage(editVillage.villageId, villageData, {
            previousData: editVillage,
            identifier: editVillage.villageName || values.villageName
          });
          setSuccessMessage('Village updated successfully!');
        } else {
          await addVillage(villageData, {
            identifier: values.villageName
          });
          setSuccessMessage('Village added successfully!');
          resetForm();
          setTalukas([]);
        }

        if (onSuccess) {
          if (onSuccessTimerRef.current != null) clearTimeout(onSuccessTimerRef.current);
          onSuccessTimerRef.current = setTimeout(() => {
            onSuccessTimerRef.current = null;
            onSuccess();
          }, 1500);
        }
      } catch (error) {
        console.error(`Error ${isEditMode ? 'updating' : 'adding'} village:`, error);
        setErrorMessage(error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'add'} village. Please try again.`);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleDistrictChange = async (event) => {
    const districtId = event.target.value;
    formik.setFieldValue('districtId', districtId);
    formik.setFieldValue('talukaId', '');

    if (districtId) {
      setIsLoadingTalukas(true);
      try {
        const response = await fetchTalukasByDistrict(districtId);
        if (!mountedRef.current) return;
        setTalukas(response.data);
      } catch (error) {
        console.error('Error fetching talukas:', error);
        if (mountedRef.current) {
          setErrorMessage('Failed to load talukas. Please try again.');
          setTalukas([]);
        }
      } finally {
        if (mountedRef.current) setIsLoadingTalukas(false);
      }
    } else {
      setTalukas([]);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  if (isLoadingDistricts) {
    return <div>Loading districts...</div>;
  }

  return (
    <FormContainer
      title={isEditMode ? 'Edit Village' : 'Add Village'}
      onSubmit={formik.handleSubmit}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={errorMessage}
      onCloseSnackbar={handleCloseSnackbar}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl
            fullWidth
            margin="normal"
            error={formik.touched.districtId && Boolean(formik.errors.districtId)}
          >
            <InputLabel id="district-select-label">District</InputLabel>
            <Select
              labelId="district-select-label"
              id="districtId"
              name="districtId"
              value={formik.values.districtId}
              onChange={handleDistrictChange}
              onBlur={formik.handleBlur}
              label="District"
            >
              {districts.map((district) => (
                <MenuItem key={district.districtId} value={district.districtId}>
                  {district.districtName}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.districtId && formik.errors.districtId && (
              <FormHelperText>{formik.errors.districtId}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl
            fullWidth
            margin="normal"
            error={formik.touched.talukaId && Boolean(formik.errors.talukaId)}
            disabled={!formik.values.districtId || isLoadingTalukas}
          >
            <InputLabel id="taluka-select-label">Taluka</InputLabel>
            <Select
              labelId="taluka-select-label"
              id="talukaId"
              name="talukaId"
              value={formik.values.talukaId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Taluka"
            >
              {talukas.map((taluka) => (
                <MenuItem key={taluka.talukaId} value={taluka.talukaId}>
                  {taluka.talukaName}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.talukaId && formik.errors.talukaId && (
              <FormHelperText>{formik.errors.talukaId}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="villageName"
            name="villageName"
            label="Village Name"
            value={formik.values.villageName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.villageName && Boolean(formik.errors.villageName)}
            helperText={formik.touched.villageName && formik.errors.villageName}
            margin="normal"
            variant="outlined"
          />
        </Grid>
      </Grid>
    </FormContainer>
  );
};

export default VillageForm;
